import { Box, Skeleton, Typography } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import queryString from 'query-string';
import { PAGE_SIZE } from 'services/constants/env';
import { getEuDdpOrdersRequest } from 'redux/reducers/euDdp/reducer';
import { getEuDdpOrders, getEuDdpOrdersIsLoad } from 'redux/reducers/euDdp/selectors';
import { saveUrl } from 'redux/reducers/auth/reducer';
import { TEuDdpOrderResponseData } from 'redux/reducers/euDdp/types';
import { TTableStructure } from 'services/constants/tableStructureTypes';
import { EasTableWithFilter } from 'ui/EasTableWithFilter';
import {
	getFiltersFromNavigateParams,
	getOrderByFromNavigateParams,
	notFixedStatusFilterBuilder,
} from 'utils/FilterParams';
import { PATHS } from 'routes/constants';
import {
	getDashboardServices,
	getDashboardServicesLoader,
} from 'redux/reducers/dashboard/selectors';
import { EU_DDP_ACTIONS, euDdpStructure } from './eu-ddp-structure';
import { NoEuDdpBanner } from './NoEuDdpBanner';
import { LastMileTrackingDialog } from './LastMileTrackingDialog';

export const EuDdp: FC = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	const { search } = location;
	const navigateParams = queryString.parse(search);
	const { page } = navigateParams;
	const tableStructure = euDdpStructure as TTableStructure<keyof TEuDdpOrderResponseData>[];

	const orders = useSelector(getEuDdpOrders);
	const ordersLoader = useSelector(getEuDdpOrdersIsLoad);
	const dashboardServices = useSelector(getDashboardServices);
	const dashboardServicesIsLoad = useSelector(getDashboardServicesLoader);

	const [prevSearch, setPrevSearch] = useState(navigateParams?.search);
	const [lastMileTrackingNumber, setLastMileTrackingNumber] = useState<string | null>(null);
	const [lastMileTrackingDialogOpen, setLastMileTrackingDialogOpen] = useState(false);
	const handleLastMileTrackingDialogClose = () => setLastMileTrackingDialogOpen(false);

	const navigateParamsWithoutPage = getFiltersFromNavigateParams(tableStructure, navigateParams, {
		customFilters: { status: notFixedStatusFilterBuilder },
	});

	const dispatchGetEuDdpOrders = () => {
		const searchStringRaw = String(navigateParams?.search ?? '');
		const searchString =
			(searchStringRaw?.length ?? 0) > 2 || (searchStringRaw?.length ?? 0) === 0
				? searchStringRaw
				: '';
		dispatch(
			getEuDdpOrdersRequest({
				pageNumber: page ? +page : 1,
				pageSize: PAGE_SIZE,
				filters: navigateParamsWithoutPage,
				searchString,
				orderBy: getOrderByFromNavigateParams(tableStructure, navigateParams),
			}),
		);
	};

	useEffect(() => {
		const prevParams = queryString.parse(prevSearch as string);
		const otherPrevParams = Object.entries(prevParams).filter(([key]) => key !== 'search');
		const otherPrevParamsString = otherPrevParams
			.map(([key, value]) => `${key}=${String(value ?? '')}`)
			.join('&');
		const currentParams = queryString.parse(location.search);
		const otherCurrentParams = Object.entries(currentParams).filter(([key]) => key !== 'search');
		const otherCurrentParamsString = otherCurrentParams
			.map(([key, value]) => `${key}=${String(value ?? '')}`)
			.join('&');

		if (
			(prevParams.search !== currentParams.search &&
				((currentParams.search as string)?.length ?? 0) > 2) ||
			((prevParams.search as string)?.length ?? 0) === 0 ||
			otherPrevParamsString !== otherCurrentParamsString
		) {
			dispatchGetEuDdpOrders();
		}
		setPrevSearch(location.search);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.search]);

	const goToOrder = (id: number) => {
		dispatch(saveUrl(window.location.pathname + window.location.search));
		navigate(PATHS.EU_DDP_ORDER_PAGE.replace(':orderId', String(id)));
	};

	const handleLink = (id: number) => {
		goToOrder(id);
	};

	const handleAction = (id: number, typeOfDialog: string) => {
		const order = orders?.data?.find((item) => item.id === id);
		if (!order) return;

		if (typeOfDialog === EU_DDP_ACTIONS.GET_LAST_MILE_TRACKING) {
			setLastMileTrackingNumber(order.last_mile_tracking_number);
			setLastMileTrackingDialogOpen(true);
			return;
		}

		if (typeOfDialog === EU_DDP_ACTIONS.TRACKING) {
			if (order.tracking_number)
				window.open(
					`https://tracking${window.location.origin.includes('dashboard.easproject.com') ? '' : '-dev'}.easproject.com/?tracking=${order.tracking_number}`,
					'_blank',
				);
			return;
		}
		goToOrder(order.id);
	};

	return (
		<Box sx={{ margin: '0px 10px 0 10px' }}>
			{dashboardServicesIsLoad && <Skeleton variant="rectangular" height={530} />}
			{!dashboardServicesIsLoad &&
				!dashboardServices?.euDdpEnabled &&
				dashboardServices?.euDdpAdvertise && <NoEuDdpBanner />}
			{!dashboardServicesIsLoad && dashboardServices?.euDdpEnabled && (
				<EasTableWithFilter<TEuDdpOrderResponseData>
					tableStructure={tableStructure}
					items={orders}
					itemsIsLoading={ordersLoader}
					handleAction={handleAction}
					handleLink={handleLink}
					minWidth={1200}
					name="order"
				>
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
							p: '2px 4px',
						}}
					>
						<Typography
							variant="h1"
							color="primary"
							sx={{ fontSize: '1.1rem', fontWeight: 'bold', textAlign: 'center', ml: 1 }}
						>
							EU DDP Shipments
						</Typography>
					</Box>
				</EasTableWithFilter>
			)}
			<LastMileTrackingDialog
				open={lastMileTrackingDialogOpen}
				setClose={handleLastMileTrackingDialogClose}
				lastMileTrackingNumber={lastMileTrackingNumber}
			/>
		</Box>
	);
};
